/**
 * Faithful re-creations of what the product actually renders.
 *
 * These are NOT screenshots. Real captures would publish the owner's VIN,
 * odometer and home location, so instead the real shapes are rebuilt here —
 * the same stat tiles, chart forms, brick heatmap and series colors the app
 * uses — driven by plausible synthetic numbers.
 *
 * Colors come from the `--viz-series-*` tokens, the same colorblind- and
 * contrast-validated slots the product's charts use, so the marketing page and
 * the app read as one system.
 *
 * Every one of these is decorative: `aria-hidden`, with the surrounding prose
 * carrying the actual meaning.
 */

const Tile = ({ label, value }: { label: string; value: string }): JSX.Element => (
  <div className="rounded-xl border border-border bg-surface-2 px-3 py-2.5">
    <div className="text-[10px] font-medium uppercase tracking-wider text-muted">
      {label}
    </div>
    <div className="mt-0.5 text-lg font-semibold">{value}</div>
  </div>
);

const Frame = ({
  title,
  right,
  children,
}: {
  title: string;
  right?: string;
  children: React.ReactNode;
}): JSX.Element => (
  <div aria-hidden className="card shadow-2xl shadow-black/20">
    <div className="mb-3 flex items-center justify-between">
      <div className="text-sm font-semibold">{title}</div>
      {right && (
        <div className="flex items-center gap-1.5 text-xs text-muted">
          <span className="h-2 w-2 rounded-full bg-emerald-400" />
          {right}
        </div>
      )}
    </div>
    {children}
  </div>
);

const Panel = ({
  label,
  children,
  className = '',
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}): JSX.Element => (
  <div
    className={`rounded-xl border border-border bg-surface-2 px-3 py-2.5 ${className}`}
  >
    <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-muted">
      {label}
    </div>
    {children}
  </div>
);

/** Recessive dashed gridlines, matching the product's chart styling. */
const Grid = (): JSX.Element => (
  <>
    {[12, 28, 44].map((y) => (
      <line
        key={y}
        x1="0"
        x2="240"
        y1={y}
        y2={y}
        stroke="rgb(var(--c-border))"
        strokeWidth="1"
        strokeDasharray="3 3"
      />
    ))}
  </>
);

const Trace = ({
  points,
  color,
  dash,
}: {
  points: string;
  color: string;
  dash?: string;
}): JSX.Element => (
  <polyline
    points={points}
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeDasharray={dash}
    strokeLinecap="round"
    strokeLinejoin="round"
    vectorEffect="non-scaling-stroke"
  />
);

const Key = ({ items }: { items: [string, string][] }): JSX.Element => (
  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
    {items.map(([label, color]) => (
      <span key={label} className="flex items-center gap-1.5 text-[10px] text-muted">
        <span
          className="inline-block h-1.5 w-3 rounded-full"
          style={{ background: color }}
        />
        {label}
      </span>
    ))}
  </div>
);

const S1 = 'var(--viz-series-1)';
const S2 = 'var(--viz-series-2)';
const S3 = 'var(--viz-series-3)';

/**
 * Hero composite: the live dashboard plus the map strip. Deliberately distinct
 * from `LiveVisual` further down the page — the same graphic twice reads as a
 * copy-paste mistake.
 */
export const HeroVisual = (): JSX.Element => (
  <Frame title="Model S" right="Live">
    <div className="grid grid-cols-2 gap-2.5">
      <Tile label="Battery" value="72%" />
      <Tile label="Range" value="218 mi" />
      <Tile label="Speed" value="58 mph" />
      <Tile label="Pack" value="396 V" />
    </div>
    <Panel label="Speed · last 5 min" className="mt-2.5">
      <svg viewBox="0 0 240 44" className="h-12 w-full" preserveAspectRatio="none">
        <Trace
          points="0,36 20,31 40,33 60,22 80,18 100,22 120,11 140,15 160,7 180,14 200,9 220,16 240,11"
          color={S1}
        />
      </svg>
    </Panel>
    {/* Route strip — the map, without publishing a real location. */}
    <div className="relative mt-2.5 h-24 overflow-hidden rounded-xl border border-border bg-gradient-to-br from-accent/20 via-surface-2 to-surface-2">
      <svg viewBox="0 0 240 96" className="h-full w-full" preserveAspectRatio="none">
        <path
          d="M12,80 C60,74 58,44 104,40 C150,36 152,20 228,14"
          fill="none"
          stroke="rgb(var(--c-accent))"
          strokeWidth="2.5"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
        <circle cx="12" cy="80" r="4" fill="rgb(var(--c-accent))" opacity="0.55" />
        <circle cx="228" cy="14" r="4.5" fill="rgb(var(--c-accent))" />
      </svg>
    </div>
  </Frame>
);

/** Live dashboard: stat tiles + a multi-series streaming chart. */
export const LiveVisual = (): JSX.Element => (
  <Frame title="Model S" right="Live">
    <div className="grid grid-cols-2 gap-2.5">
      <Tile label="Battery" value="72%" />
      <Tile label="Range" value="218 mi" />
      <Tile label="Speed" value="58 mph" />
      <Tile label="Pack" value="396 V" />
    </div>
    <Panel label="Speed · power · last 5 min" className="mt-2.5">
      <svg viewBox="0 0 240 56" className="h-16 w-full" preserveAspectRatio="none">
        <Grid />
        <Trace
          points="0,46 20,40 40,42 60,30 80,26 100,30 120,18 140,22 160,12 180,20 200,14 220,22 240,16"
          color={S1}
        />
        <Trace
          points="0,50 20,47 40,38 60,41 80,33 100,36 120,26 140,31 160,22 180,29 200,24 220,30 240,25"
          color={S2}
        />
      </svg>
      <Key items={[['Speed', S1], ['Pack power', S2]]} />
    </Panel>
  </Frame>
);

/** Trips: a drive row plus an efficiency trend. */
export const TripsVisual = (): JSX.Element => (
  <Frame title="Trips">
    <div className="space-y-2">
      {[
        ['Tue 08:14', '18.4 mi', '312 Wh/mi'],
        ['Mon 17:52', '31.9 mi', '287 Wh/mi'],
        ['Mon 08:06', '18.1 mi', '341 Wh/mi'],
      ].map(([when, dist, eff]) => (
        <div
          key={when}
          className="flex items-center justify-between rounded-xl border border-border bg-surface-2 px-3 py-2 text-xs"
        >
          <span className="text-muted">{when}</span>
          <span className="font-semibold">{dist}</span>
          <span className="font-mono text-muted">{eff}</span>
        </div>
      ))}
    </div>
    <Panel label="Efficiency · last 30 days" className="mt-2.5">
      <svg viewBox="0 0 240 56" className="h-14 w-full" preserveAspectRatio="none">
        <Grid />
        <Trace
          points="0,34 24,30 48,36 72,28 96,32 120,24 144,29 168,20 192,26 216,19 240,23"
          color={S1}
        />
      </svg>
      <Key items={[['Wh/mi', S1]]} />
    </Panel>
  </Frame>
);

/**
 * Diagnostics: pack brick balance as a heatmap, plus the three-motor thermal
 * chart — the two views the product actually renders for pack and powertrain
 * health.
 */
export const DiagnosticsVisual = (): JSX.Element => {
  // Deterministic pseudo-spread so the heatmap looks like a real pack: mostly
  // balanced, a couple of cooler outliers.
  const bricks = Array.from({ length: 48 }, (_, i) => {
    const wave = Math.sin(i * 0.7) * 0.35 + Math.sin(i * 0.23) * 0.3;
    const outlier = i === 17 || i === 38 ? -0.75 : 0;
    return Math.max(-1, Math.min(1, wave + outlier));
  });
  const shade = (v: number): string =>
    v < -0.5
      ? 'rgb(var(--c-accent) / 0.85)'
      : v < -0.15
        ? 'rgb(var(--c-accent) / 0.5)'
        : v < 0.3
          ? 'rgb(var(--c-accent) / 0.28)'
          : 'rgb(var(--c-accent) / 0.14)';

  return (
    <Frame title="Diagnostics">
      <Panel label="Pack balance · 48 bricks">
        <div className="grid grid-cols-12 gap-1">
          {bricks.map((v, i) => (
            <span
              key={i}
              className="aspect-square rounded-[3px]"
              style={{ background: shade(v) }}
            />
          ))}
        </div>
        <div className="mt-2 flex justify-between text-[10px] text-muted">
          <span>4.076 V min</span>
          <span>2 mV spread</span>
          <span>4.078 V max</span>
        </div>
      </Panel>
      <Panel label="Motor temps · front / rear ×2" className="mt-2.5">
        <svg viewBox="0 0 240 56" className="h-16 w-full" preserveAspectRatio="none">
          <Grid />
          <Trace
            points="0,44 24,42 48,36 72,30 96,24 120,20 144,18 168,22 192,27 216,33 240,38"
            color={S1}
          />
          <Trace
            points="0,45 24,43 48,38 72,33 96,27 120,22 144,19 168,23 192,28 216,34 240,39"
            color={S2}
            dash="4 3"
          />
          <Trace
            points="0,46 24,44 48,39 72,34 96,29 120,24 144,21 168,25 192,30 216,36 240,41"
            color={S3}
            dash="1 3"
          />
        </svg>
        <Key items={[['Front', S1], ['Rear L', S2], ['Rear R', S3]]} />
      </Panel>
    </Frame>
  );
};
